import React, { useState } from 'react'
import { Activity, X } from 'lucide-react'
import { Tooltip } from '../../ui'
import BroadcastPlayer from '../BroadcastPlayer'
import broadcastService, {
  PublishedBroadcast, ExposureTimeline as TimelineData
} from '../../../services/broadcastService'

interface Props {
  broadcast: PublishedBroadcast
  timeline: TimelineData
  customerId: string
}

/**
 * Sponsor presence across the running time of the broadcast.
 *
 * WHAT IT ADDS OVER THE KPI. "Sponsor on screen 48%" is one number for a
 * three-hour broadcast, and the thing worth knowing is not the average but
 * WHERE THE GAPS ARE. A studio segment carries no signage; an interview
 * block carries none; both are unsold inventory, and neither is visible in
 * an average. This is the chart a rightsholder uses to find them.
 *
 * HEIGHT IS A SHARE, NOT A COUNT. Buckets vary in width with the length of
 * the broadcast, so a raw count of sponsor-seconds would mean different
 * things at different zoom levels and could not be compared across
 * broadcasts. Share of the frames in that bucket is the same question at
 * every scale.
 *
 * SHADED BY SCENE, because a run of short bars is a different story
 * depending on what the camera was doing. Unexplained dead air is a problem;
 * a studio segment is simply a part of the show with no boards in it.
 */

/** Non-play scenes get a wash behind the bars. Live play gets none — it is
 *  the baseline, and shading everything shades nothing. */
const SCENE_WASH: Record<string, string> = {
  studio: 'bg-amber-500/10',
  replay: 'bg-blue-500/10',
  crowd: 'bg-violet-500/25',
  commercial: 'bg-rose-500/10',
  graphic: 'bg-slate-500/25'
}

const SCENE_LABEL: Record<string, string> = {
  live_play: 'Live play', studio: 'Studio', replay: 'Replay',
  crowd: 'Crowd', commercial: 'Commercial', graphic: 'Graphic', unknown: 'Unclassified'
}

const clock = (ms: number) => {
  const t = Math.floor(ms / 1000)
  const h = Math.floor(t / 3600)
  const m = Math.floor((t % 3600) / 60)
  const s = t % 60
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${m}:${String(s).padStart(2, '0')}`
}

const ExposureTimeline: React.FC<Props> = ({ broadcast, timeline, customerId }) => {
  const [watchAt, setWatchAt] = useState<number | null>(null)
  const { bucketMs, buckets } = timeline

  if (!buckets || buckets.length === 0) return null

  const duration = broadcast.duration_ms ?? (buckets.length * bucketMs)

  // The bucket width in words, so the reader knows what one bar is before
  // reading any of them.
  const unit = bucketMs >= 60_000
    ? `${Math.round(bucketMs / 60_000)} min`
    : `${Math.round(bucketMs / 1000)}s`

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
            <Activity className="h-4 w-4 text-gray-400" strokeWidth={1.75} />
            Sponsor exposure through the broadcast
          </h3>
          <p className="text-[11px] text-gray-500 mt-0.5">
            Sponsored brands on screen, per {unit} · click a bar to watch from there
          </p>
        </div>
      </div>

      <div className="flex items-end gap-px h-24 bg-gray-50/60 rounded px-1 pt-1">
        {buckets.map((b) => {
          const share = b.frames > 0 ? b.sponsor_frames / b.frames : 0
          const wash = SCENE_WASH[b.scene ?? ''] ?? ''
          return (
            <Tooltip
              key={b.start_ms}
              placement="top"
              maxWidth={220}
              wrapperClassName="flex-1 h-full flex items-end"
              content={
                <div className="text-left">
                  <p className="font-semibold">{clock(Number(b.start_ms))}</p>
                  <p>{b.sponsor_frames} of {b.frames}s with a sponsor on screen</p>
                  <p className="text-gray-300">{SCENE_LABEL[b.scene ?? 'unknown'] ?? b.scene}</p>
                </div>
              }
            >
              <button
                type="button"
                onClick={() => setWatchAt(Number(b.start_ms))}
                aria-label={`Watch from ${clock(Number(b.start_ms))}`}
                className={`w-full h-full flex items-end ${wash} hover:bg-blue-500/20 transition-colors`}
              >
                {/* A floor of 2px on a non-zero bar: one second in a long
                    bucket is real exposure and must not render as nothing,
                    which would make it indistinguishable from a gap. */}
                <span
                  className="w-full bg-indigo-500 rounded-sm"
                  style={{ height: share > 0 ? `max(2px, ${(share * 100).toFixed(1)}%)` : 0 }}
                />
              </button>
            </Tooltip>
          )
        })}
      </div>

      <div className="flex justify-between text-[10px] text-gray-400 font-mono mt-1 px-1">
        <span>0:00</span>
        <span>{clock(duration / 2)}</span>
        <span>{clock(duration)}</span>
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2.5 text-[10px] text-gray-500">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-2.5 h-2 rounded-sm bg-indigo-500" />sponsor on screen
        </span>
        {Object.entries(SCENE_WASH)
          .filter(([code]) => buckets.some((b) => b.scene === code))
          .map(([code, cls]) => (
            <span key={code} className="inline-flex items-center gap-1">
              <span className={`inline-block w-2.5 h-2 rounded-sm ${cls} border border-gray-200`} />
              {SCENE_LABEL[code] ?? code}
            </span>
          ))}
      </div>

      {watchAt !== null && (
        <div className="mt-3 border-t border-gray-100 pt-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-gray-700">
              Watching from {clock(watchAt)}
            </p>
            <button onClick={() => setWatchAt(null)}
              className="p-1 text-gray-400 hover:text-gray-700 rounded">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="aspect-video bg-black rounded-lg overflow-hidden">
            <BroadcastPlayer
              fetchUrl={async () =>
                (await broadcastService.getPlaybackUrl(customerId, broadcast.id)).data}
              startMs={watchAt}
              className="w-full h-full"
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default ExposureTimeline
